// ---------------------------------------------------------------------------
// TestSetsPage — Browse, search, and manage evaluation test sets.
//
// Mirrors DatasetsPage layout (3-column: list + detail). Reuses DatasetList,
// DatasetSearch, and DatasetFilter since TestSet extends Dataset.
// ---------------------------------------------------------------------------

import {
  Button,
  mergeClasses,
  Spinner,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Database24Filled,
  Database24Regular,
  Filter20Filled,
  Filter20Regular,
  Info16Regular,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { isAccessDeniedStatus } from '../../api/errors.js';
// Imported from its own module, not the barrel, so page tests that stub the
// barrel still render the real entitlement message.
import { DatalakeAccessDenied } from '../../components/Datalake/DatalakeAccessDenied/DatalakeAccessDenied.js';
import type {
  DatasetAccessFilter,
  DatasetDataType,
} from '../../components/Datalake/DatasetFilter/DatasetFilter.interface.js';
import {
  DatasetFilter,
  DatasetList,
  DatasetSearch,
} from '../../components/Datalake/index.js';
import type { TestSetOwnershipFilter } from '../../constants/index.js';
import { TESTSET_OWNERSHIP_FILTERS } from '../../constants/index.js';
import {
  DataHubClientProvider,
  useDataHubClient,
  useDataHubUserObjectId,
} from '../../context/DataHubClientContext.js';
import { useTestSet } from '../../hooks/useTestSet/useTestSet.js';
import { useTestSets } from '../../hooks/useTestSets/useTestSets.js';
import type { Dataset } from '../../services/dataHubService.interface.js';
import type { TestSet } from '../../services/testSetService.interface.js';
import { TestSetService } from '../../services/testSetService.js';

import { CreateTestSetDialog } from './CreateTestSetDialog/CreateTestSetDialog.js';
import { TestSetDetailView } from './TestSetDetailView/TestSetDetailView.js';
import { useStyles } from './TestSetsPage.style.js';

const FilterIcon = bundleIcon(Filter20Filled, Filter20Regular) as React.FC;
const DatabaseIcon = bundleIcon(
  Database24Filled,
  Database24Regular,
) as React.FC;

export function TestSetsPage() {
  return (
    <DataHubClientProvider>
      <TestSetsPageContent />
    </DataHubClientProvider>
  );
}

function TestSetsPageContent() {
  const styles = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const client = useDataHubClient();
  const userObjectId = useDataHubUserObjectId();
  const service = useMemo(() => new TestSetService(client), [client]);

  const selectedTestSetId = searchParams.get('testSetId');

  const [query, setQuery] = useState('');
  const [accessFilter, setAccessFilter] = useState<DatasetAccessFilter>('All');
  const [dataType, setDataType] = useState<DatasetDataType>('All types');
  const [ownershipFilter, setOwnershipFilter] =
    useState<TestSetOwnershipFilter>('All Test Sets');
  const [showFilters, setShowFilters] = useState(false);
  const [altCount, setAltCount] = useState<number | null>(null);
  const [tokenReady, setTokenReady] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const {
    testSets,
    totalCount,
    isLoading,
    error,
    errorStatusCode,
    isLoadingMore,
    hasMore,
    loadMore,
    refresh,
  } = useTestSets({
    query,
    accessFilter,
    dataType,
    ownershipFilter:
      ownershipFilter === 'My Test Sets' ? 'My Datasets' : 'All Datasets',
    userObjectId,
  });

  useEffect(() => {
    if (!isLoading) setTokenReady(true);
  }, [isLoading]);

  useEffect(() => {
    if (!tokenReady) return;

    let cancelled = false;
    setAltCount(null);

    const altOwnerObjectId =
      ownershipFilter === 'All Test Sets'
        ? (userObjectId ?? undefined)
        : undefined;

    service
      .searchTestSets({
        query: undefined,
        ownerUserObjectId: altOwnerObjectId,
        pageSize: 100,
        includeCount: true,
      })
      .then((result) => {
        if (!cancelled) setAltCount(result.totalCount ?? result.items.length);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [service, ownershipFilter, userObjectId, tokenReady]);

  const effectiveTotal = totalCount ?? (!hasMore ? testSets.length : null);
  const allCount =
    ownershipFilter === 'All Test Sets' ? effectiveTotal : altCount;
  const myCount =
    ownershipFilter === 'My Test Sets' ? effectiveTotal : altCount;

  const handleTestSetClick = useCallback(
    (item: Dataset) => {
      setSearchParams({ testSetId: item.id });
    },
    [setSearchParams],
  );

  const handleTestSetCreated = useCallback(
    (_testSet: TestSet) => {
      refresh();
    },
    [refresh],
  );

  const handleTestSetDeleted = useCallback(() => {
    setSearchParams({});
    refresh();
  }, [setSearchParams, refresh]);

  const countLabel =
    effectiveTotal !== null
      ? `Showing ${testSets.length} of ${effectiveTotal}`
      : `${testSets.length} test sets`;

  return (
    <div className={styles.root}>
      <div className={styles.panelsRow}>
        {/* ─── Left panel: Test set list ────────────────────────────── */}
        <div className={styles.leftPanel}>
          <div className={styles.leftHeader}>
            <Text as="span" className={styles.leftHeaderTitle}>
              Test Sets
            </Text>
            <Button
              appearance="primary"
              size="small"
              onClick={() => setShowCreateDialog(true)}
            >
              New Test Set
            </Button>
          </div>

          <div className={styles.searchRow}>
            <DatasetSearch
              value={query}
              onChange={setQuery}
              placeholder="Search test sets..."
              label="Search test sets"
            />
            <Tooltip
              content={showFilters ? 'Hide filters' : 'Show filters'}
              relationship="label"
            >
              <Button
                appearance={showFilters ? 'primary' : 'subtle'}
                size="small"
                icon={<FilterIcon />}
                onClick={() => {
                  setShowFilters((v) => !v);
                }}
                aria-pressed={showFilters}
                aria-label="Toggle filters"
              />
            </Tooltip>
          </div>

          {/* Ownership pills */}
          <div className={styles.ownershipBar}>
            {TESTSET_OWNERSHIP_FILTERS.map((filter) => (
              <Button
                key={filter}
                size="small"
                className={mergeClasses(
                  styles.ownershipPill,
                  ownershipFilter === filter && styles.ownershipPillActive,
                )}
                onClick={() => {
                  setOwnershipFilter(filter);
                }}
                aria-pressed={ownershipFilter === filter}
              >
                {filter}
                {filter === 'All Test Sets' && allCount !== null
                  ? ` (${allCount})`
                  : ''}
                {filter === 'My Test Sets' && myCount !== null
                  ? ` (${myCount})`
                  : ''}
              </Button>
            ))}
          </div>

          {showFilters && (
            <div className={styles.filterRow}>
              <DatasetFilter
                dataType={dataType}
                onDataTypeChange={setDataType}
                accessFilter={accessFilter}
                onAccessFilterChange={setAccessFilter}
              />
            </div>
          )}

          {!isLoading && error === null && (
            <Text as="span" className={styles.countText}>
              {countLabel}
              {hasMore && (
                <Tooltip
                  content="Not all test sets are loaded yet. Click 'Load more' at the bottom of the list to fetch additional results."
                  relationship="description"
                >
                  <Info16Regular className={styles.infoIcon} />
                </Tooltip>
              )}
            </Text>
          )}

          <div className={styles.listContainer}>
            {isAccessDeniedStatus(errorStatusCode) ? (
              <div className={styles.listAccessDenied}>
                <DatalakeAccessDenied action="access test sets" />
              </div>
            ) : (
              <DatasetList
                datasets={testSets}
                isLoading={isLoading}
                error={error}
                selectedId={selectedTestSetId ?? undefined}
                onDatasetClick={handleTestSetClick}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                onLoadMore={loadMore}
              />
            )}
          </div>
        </div>

        {/* ─── Center panel: Detail view ───────────────────────────── */}
        <div className={styles.centerPanel}>
          {selectedTestSetId ? (
            <SelectedTestSetDetail
              testSetId={selectedTestSetId}
              service={service}
              onDeleted={handleTestSetDeleted}
            />
          ) : (
            <div className={styles.centerEmpty}>
              <div className={styles.centerEmptyIcon}>
                <DatabaseIcon />
              </div>
              <span className={styles.centerEmptyText}>
                Select a test set to view
              </span>
              <span className={styles.centerEmptySub}>
                Choose a test set from the list to see its data items, overview,
                permissions, and attributes.
              </span>
            </div>
          )}
        </div>
      </div>

      <CreateTestSetDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleTestSetCreated}
      />
    </div>
  );
}

function SelectedTestSetDetail({
  testSetId,
  service,
  onDeleted,
}: {
  testSetId: string;
  service: TestSetService;
  onDeleted: () => void;
}) {
  const styles = useStyles();
  const { testSet, isLoading, error, errorStatusCode, refetch } =
    useTestSet(testSetId);

  if (isLoading) {
    return (
      <div className={styles.detailLoading}>
        <Spinner label="Loading test set..." />
      </div>
    );
  }

  if (errorStatusCode === 401 || errorStatusCode === 403) {
    return (
      <div className={styles.detailAccessDenied}>
        <Text weight="semibold" size={400}>
          Access denied
        </Text>
        <Text size={300}>
          You don&apos;t have permission to view this test set. Contact the
          owner to request access.
        </Text>
      </div>
    );
  }

  if (error !== null || testSet === null) {
    return (
      <div className={styles.detailError}>
        Failed to load test set: {error ?? 'Not found'}
      </div>
    );
  }

  return (
    <TestSetDetailView
      testSet={testSet}
      service={service}
      onDeleted={onDeleted}
      onRefetch={refetch}
    />
  );
}
